
import { IStaffPayrollPrintData } from 'features/report/services/types/reportStaff';
import shopSelectors from 'services/shop/selectors';
import { EHideFieldReport, isHideFieldReport } from 'utils/hide-field-report';
import { IUIStaffFieldItem } from '../StaffPayrollDetail';
import { ESalaryPayrollType } from 'features/settingStaff/services/constants';
import { IParamsReportSalaryStaff } from 'features/report/services/types/review';
import { useMemo } from 'react';

const useDefaultPayrollTemplate = (params: IParamsReportSalaryStaff) => {
  const allSetting = shopSelectors.data.allSetting();
  const shopInfo = shopSelectors.data.shopInfo();
  const listHideFieldReport = shopSelectors.getListHideFieldReport();
  const staffPayrollFields: IUIStaffFieldItem<IStaffPayrollPrintData>[] = useMemo(() => {
    const cashLabel = allSetting?.labelSalaryCashOrBonus || 'Cash';
    const singleStaff = !!params.staffId;
    return [
      {
        label: 'TOTAL SALES',
        fieldName: 'totalSales',
        component: 'ReportPayrollTitleRow',
        hidden: (data) => isHideFieldReport(listHideFieldReport, EHideFieldReport.TOTAL_SALE)
          || !data?.totalSales,
      },
      {
        label: 'SUPPLY FEES',
        fieldName: 'supplyFee',
        component: 'ReportPayrollTitleRow',
        hidden: (data) => isHideFieldReport(listHideFieldReport, EHideFieldReport.SUPPLY_FEE)
          || !data?.supplyFee,
      },
      {
        label: 'SALE ADJUSTMENT',
        fieldName: 'saleAdjustment',
        component: 'ReportPayrollTitleRow',
        hidden: (data) => !data?.saleAdjustment,
      },
      {
        label: 'TOTAL',
        fieldName: (data) => {
          const total =
            (data?.totalSales || 0) -
            (data?.supplyFee || 0) +
            (data?.saleAdjustment || 0);
          if (shopInfo?.id === 'df00da9a-fe76-48d9-99bb-8278ec12a03b') {
            return total - (data?.staffDiscount || 0);
          }

          return total;
        },
        component: 'ReportPayrollCaption2HighLightRow',
        hidden: isHideFieldReport(
          listHideFieldReport,
          EHideFieldReport.TOTAL
        ),
        className: 'TOTAL_ROW',
      },
      {
        label: (data) => {
          const hideCommissionRate = isHideFieldReport(
            listHideFieldReport,
            EHideFieldReport.SHOW_PERCENTAGE
          );
          return hideCommissionRate
            ? 'COMMISSION'
            : `COMMISSION (${data?.commissionPercent}%)`;
        },
        fieldName: 'totalCommission',
        component: 'ReportPayrollTitleRow',
        hidden: (data) =>
          isHideFieldReport(listHideFieldReport, EHideFieldReport.COMMISSION)
          || !data?.totalCommission,
        className: 'COMMISSION_ROW',
      },
      {
        label: 'DISCOUNT',
        fieldName: 'staffDiscount',
        component: 'ReportPayrollTitleRow',
        hidden: (data) => isHideFieldReport(listHideFieldReport, EHideFieldReport.DISCOUNT) || !data?.staffDiscount,
      },
      {
        hidden: isHideFieldReport(listHideFieldReport, EHideFieldReport.TIP),
        fields: [
          {
            label: 'COLLECTED TIPS',
            fieldName: 'tipCollected',
            component: 'ReportPayrollTipFeeRow',
            hidden: (data) => isHideFieldReport(listHideFieldReport, EHideFieldReport.TIP_COLLECTED) || !data?.tipCollected,
          },
          {
            label: (data) => `TIP FEE ${(data?.tipFeeType === 'PERCENT' && `(${data?.tipFeeValue || 0}%)`)}`,
            fieldName: 'tipFee',
            component: 'ReportPayrollTipFeeRow',
            hidden: (data) => !data?.tipFee,
          },
          {
            label: 'TOTAL TIP',
            fieldName: 'totalTip',
            component: 'ReportPayrollTipFeeRow',
            hidden: (data) => !data?.tipFee,
          },
        ]
      },
      {
        label: 'CLEANING FEE',
        fieldName: 'totalFeeCleaning',
        component: 'ReportPayrollTipFeeRow',
        hidden: (data) => isHideFieldReport(listHideFieldReport, EHideFieldReport.CLEANING_FEE) || !data?.totalFeeCleaning,
      },
      {
        label: 'SUPPLY SHARE',
        fieldName: 'supplyShareFee',
        component: 'ReportPayrollTipFeeRow',
        hidden: (data) => !data?.supplyShareFee,
      },
      {
        label: 'PAYMENT TYPES',
        fieldName: () => ' ',
        hideValue: true,
        component: 'ReportPayrollCaption2HighLightRow',
        hidden: isHideFieldReport(listHideFieldReport, EHideFieldReport.PAYMENT_TYPE) || !singleStaff,
        fields: [
           {
            label: 'Credit Card',
            fieldName: 'creditAmount',
            component: 'ReportPayrollPaymentRow',
          },
          {
            label: 'Cash',
            fieldName: 'cashAmount',
            component: 'ReportPayrollPaymentRow',
          },
          {
            label: 'Gift Card',
            fieldName: 'giftCardAmount',
            component: 'ReportPayrollPaymentRow',
          },
          {
            label: 'Loyalty',
            fieldName: 'loyaltyAmount',
            component: 'ReportPayrollPaymentRow',
            hidden: (data) => !data?.loyaltyAmount,
          },
          {
            label: 'Check',
            fieldName: 'checkAmount',
            component: 'ReportPayrollPaymentRow',
            hidden: (data) => !data?.checkAmount,
          },
          {
            label: 'Other',
            fieldName: 'otherAmount',
            component: 'ReportPayrollPaymentRow',
          },
        ]
      },
      {
        label: 'PAYROLL',
        fieldName: 'totalPayroll',
        component: 'ReportPayrollCaption2HighLightRow',
        hidden: (data) => isHideFieldReport(listHideFieldReport, EHideFieldReport.PAYROLL) || !data?.totalPayroll,
      },
      {
        label: (data) => {
          const hideBonusPercent = isHideFieldReport(
            listHideFieldReport,
            EHideFieldReport.SHOW_PERCENTAGE
          );
          return hideBonusPercent
            ? cashLabel
            : `${cashLabel} (${data?.cashPercent || 0}%)`;
        },
        fieldName: 'cashValue',
        component: 'ReportPayrollCaption3Row',
        hidden: (data) => isHideFieldReport(listHideFieldReport, EHideFieldReport.BONUS) || !data?.totalCash,
        className: 'PAYROLL_BONUS',
      },
      {
        label: (data) => {
          const hideCheckPercent = isHideFieldReport(
            listHideFieldReport,
            EHideFieldReport.SHOW_PERCENTAGE
          );
          return hideCheckPercent
            ? 'Check'
            : `Check (${data?.checkPercent || 0}%)`;
        },
        fieldName: 'checkValue',
        component: 'ReportPayrollCaption3Row',
        hidden: (data) => isHideFieldReport(listHideFieldReport, EHideFieldReport.CHECK) || !data?.checkValue,
        className: 'PAYROLL_CHECK',
      },
      {
        label: (data) => {
          const hideTipToCashPercent = isHideFieldReport(
            listHideFieldReport,
            EHideFieldReport.SHOW_PERCENTAGE
          );
          return hideTipToCashPercent
            ? `Tip to ${cashLabel}`
            : `Tip to ${cashLabel} ${data?.tipCashPercent ? ` (${data?.tipCashPercent}%)` : ''}`;
        },
        fieldName: 'tipToCashValue',
        component: 'ReportPayrollCaption3Row',
        hidden: (data) => isHideFieldReport(listHideFieldReport, EHideFieldReport.TIP_TO_BONUS) || !data?.tipToCashValue,
        className: 'PAYROLL_TIP_TO_CASH',
      },
      {
        label: (data) => {
          const hideTipToCheckPercent = isHideFieldReport(
            listHideFieldReport,
            EHideFieldReport.SHOW_PERCENTAGE
          );
          return hideTipToCheckPercent
            ? 'Tip to Check'
            : `Tip to Check ${data?.tipCheckPercent ? ` (${data?.tipCheckPercent}%)` : ''}`;
        },
        fieldName: 'tipToCheckValue',
        component: 'ReportPayrollCaption3Row',
        hidden: (data) => isHideFieldReport(listHideFieldReport, EHideFieldReport.TIP_TO_CHECK) || !data?.tipToCheckValue,
        className: 'PAYROLL_TIP_TO_CHECK',
      },
      {
        bt: 'print_line',
        hidden: (data) => data?.payrollType !== ESalaryPayrollType.W2,
        fields: [
          {
            label: (data) => `Federal Tax Withheld (${data?.tax?.federalTaxPercent || 0}%)`,
            fieldName: ['tax', 'federalTax'],
            component: 'ReportPayrollCaption2Row',
            hidden: (data) => isHideFieldReport(listHideFieldReport, EHideFieldReport.FEDERAL_TAX_WITHHELD) || !data?.tax?.federalTax,
          },
          {
            label: (data) => `Social Security Tax Withheld (${data?.tax?.socialTaxPercent || 0}%)`,
            fieldName: ['tax', 'socialTax'],
            component: 'ReportPayrollCaption2Row',
            hidden: (data) => isHideFieldReport(listHideFieldReport, EHideFieldReport.SOCIAL_SECURITY_TAX_WITHHELD) || !data?.tax?.socialTax,
          },
          {
            label: (data) => `Medicare Tax Withheld (${data?.tax?.medicalTaxPercent || 0}%)`,
            fieldName: ['tax', 'medicalTax'],
            component: 'ReportPayrollCaption2Row',
            hidden: (data) => isHideFieldReport(listHideFieldReport, EHideFieldReport.MEDICARE_TAX_WITHHELD) || !data?.tax?.medicalTax,
          },
          {
            label: `Total ${cashLabel}`,
            fieldName: 'totalCash',
            component: 'ReportPayrollCaption2Row',
            className: 'TOTAL_CASH',
            hidden: (data) => isHideFieldReport(listHideFieldReport, EHideFieldReport.BONUS) || !data?.totalCash,
          },
          {
            label: 'Total Check',
            fieldName: 'totalCheck',
            component: 'ReportPayrollCaption2Row',
            className: 'TOTAL_CHECK',
            hidden: (data) => isHideFieldReport(listHideFieldReport, EHideFieldReport.CHECK) || !data?.totalCheck,
          },
        ]
      },
      {
        bt: 'print_line',
        hidden: (data) => data?.payrollType === ESalaryPayrollType.W2,
        fields: [
          {
            label: `Total ${cashLabel}`,
            fieldName: 'totalCash',
            component: 'ReportPayrollCaption2Row',
            className: 'TOTAL_CASH',
            hidden: (data) => isHideFieldReport(listHideFieldReport, EHideFieldReport.BONUS) || !data?.totalCash,
          },
          {
            label: 'Total Check',
            fieldName: 'totalCheck',
            component: 'ReportPayrollCaption2Row',
            className: 'TOTAL_CHECK',
            hidden: (data) => isHideFieldReport(listHideFieldReport, EHideFieldReport.CHECK) || !data?.totalCheck,
          },
        ]
      },
    ];
  }, [params, params.staffId, allSetting, listHideFieldReport]);

  return staffPayrollFields;
};

export default useDefaultPayrollTemplate;